import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Field, TextInput, TextArea, SelectInput, ActionButton } from "../common";
import { LocationPicker } from "../LocationPicker";
import { RequesterPicker } from "../RequesterPicker";
import { JOB_TYPE_OPTIONS } from "../../lib/rentalDispatch";

const STAGE_OPTS = [
  { value: "A", label: "Stage A" },
  { value: "B", label: "Stage B" },
  { value: "C", label: "Stage C" },
  { value: "D", label: "Stage D" },
];

export const RentalEditForm = ({ dispatch, customerOptions, onSaved, onCancel }) => {
  const [form, setForm] = useState({
    rental_type: dispatch.rental_type || "frac_tank",
    customer_id: dispatch.customer_id || "",
    delivery_location: dispatch.delivery_location || "",
    location_id: dispatch.location_id || "",
    location_name: dispatch.location_name || "",
    initial_stage: dispatch.initial_stage || dispatch.current_stage || "A",
    lat: dispatch.lat != null ? String(dispatch.lat) : "",
    lng: dispatch.lng != null ? String(dispatch.lng) : "",
    requester_id: dispatch.requester_id || "",
    company_man: dispatch.company_man || "",
    order_taken_date: (dispatch.order_taken_date || "").slice(0, 10),
    deliver_by_date: (dispatch.deliver_by_date || "").slice(0, 10),
    billing_start_date: (dispatch.billing_start_date || "").slice(0, 10),
    notes: dispatch.notes || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        rental_type: form.rental_type,
        customer_id: form.customer_id,
        delivery_location: form.delivery_location,
        initial_stage: form.initial_stage || "A",
        requester_id: form.requester_id || null,
        notes: form.notes,
      };
      if (form.location_id) payload.location_id = form.location_id;
      if (form.lat !== "") payload.lat = parseFloat(form.lat);
      if (form.lng !== "") payload.lng = parseFloat(form.lng);
      if (form.order_taken_date) payload.order_taken_date = form.order_taken_date;
      if (form.deliver_by_date) payload.deliver_by_date = form.deliver_by_date;
      if (form.billing_start_date) payload.billing_start_date = form.billing_start_date;
      const { data } = await api.patch(`/fractank/dispatches/${dispatch.id}`, payload);
      toast.success(`${dispatch.dispatch_number} updated`);
      onSaved?.(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-4" data-testid="rental-edit-form">
      <Field label="Rental Type"><SelectInput testId="rental-edit-type" value={form.rental_type} onChange={(e) => setForm({ ...form, rental_type: e.target.value })} className="w-full min-w-0 max-w-full !py-3 !text-base" options={JOB_TYPE_OPTIONS} /></Field>
      <Field label="Customer">
        <SelectInput testId="rental-edit-customer" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value, delivery_location: "", location_id: "", location_name: "", requester_id: "", company_man: "" })} required placeholder="Select customer" className="w-full min-w-0 max-w-full !py-3 !text-base" options={customerOptions} />
      </Field>
      <LocationPicker testId="rental-edit" customerId={form.customer_id} value={form.location_id} snapshotName={form.location_name || form.delivery_location} onChange={(loc) => setForm((prev) => ({ ...prev, location_id: loc?.id || "", location_name: loc?.name || "", delivery_location: loc ? (loc.name + (loc.wells?.length ? " - " + loc.wells.join(", ") : "")) : "", lat: loc?.lat != null ? String(loc.lat) : prev.lat, lng: loc?.lng != null ? String(loc.lng) : prev.lng }))} />
      <Field label="Starting Well Stage (editable later)"><SelectInput testId="rental-edit-stage" value={form.initial_stage || "A"} onChange={(e) => setForm({ ...form, initial_stage: e.target.value })} className="w-full min-w-0 max-w-full !py-3 !text-base" options={STAGE_OPTS} /></Field>
      <div className="grid grid-cols-2 gap-3 [&>label]:min-w-0">
        <Field label="Latitude (GPS)"><TextInput testId="rental-edit-lat" type="number" step="any" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} className="w-full min-w-0 max-w-full !py-3 !text-base" placeholder="31.9973" /></Field>
        <Field label="Longitude (GPS)"><TextInput testId="rental-edit-lng" type="number" step="any" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} className="w-full min-w-0 max-w-full !py-3 !text-base" placeholder="-102.0779" /></Field>
      </div>
      <RequesterPicker testId="rental-edit" customerId={form.customer_id} value={form.requester_id} snapshotName={form.company_man} onChange={(r) => setForm((f) => ({ ...f, requester_id: r?.id || "", company_man: r?.name || "" }))} />
      <Field label="Order taken date"><TextInput testId="rental-edit-order-taken" type="date" value={form.order_taken_date} onChange={(e) => setForm({ ...form, order_taken_date: e.target.value })} className="w-full min-w-0 max-w-full !py-3 !text-base" /></Field>
      <Field label="Date to be delivered"><TextInput testId="rental-edit-deliver-by" type="date" value={form.deliver_by_date} onChange={(e) => setForm({ ...form, deliver_by_date: e.target.value })} className="w-full min-w-0 max-w-full !py-3 !text-base" /></Field>
      <Field label="Date billing rental starts"><TextInput testId="rental-edit-billing-start" type="date" value={form.billing_start_date} onChange={(e) => setForm({ ...form, billing_start_date: e.target.value })} className="w-full min-w-0 max-w-full !py-3 !text-base" /></Field>
      <Field label="Notes"><TextArea testId="rental-edit-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full min-w-0 max-w-full !text-base" placeholder="Press Enter for a new line…" /></Field>
      <div className="flex gap-2">
        <ActionButton type="button" variant="secondary" testId="rental-edit-cancel" onClick={onCancel} disabled={busy}>Cancel</ActionButton>
        <ActionButton type="submit" testId="rental-edit-save" disabled={busy} className="flex-1 justify-center">{busy ? "Saving…" : "Save Changes"}</ActionButton>
      </div>
    </form>
  );
};
